import React from "react";
import { object } from "yup";
import Helpers from "../../../commons/helpers";
import { IResource } from "../../../commons/interfaces";
import { multiLangSchema } from "../../../commons/yup";
import { Constants } from "../../../constants/constants";
import { AvailableLanguage, Mode } from "../../../constants/enums";
import { useCommonComponentContext } from "../../../providers/CommonComponentProvider";
import useStrings from "../../useStrings";
import ResourceService, { IRequestUpdateResource } from "./../../../services/identity/resourceService";
import { IResourceError } from "./useData";

const REQUIRED_MULTI_LANG_KEY = ["resourceName"];

export const useSubmit = ({
    data,
    mode,
    onChangeMode,
    onGoBack,
    setError,
    setLanguage,
}: {
    data: IResource;
    mode: Mode;
    onChangeMode: (mode: Mode) => void;
    onGoBack: () => void;
    setLanguage: (value: string) => void;
    setError: React.Dispatch<React.SetStateAction<IResourceError>>;
}) => {
    const Strings = useStrings();
    const { clientId, userInfo, serviceCode, onError, onSuccess, onShowLoading, onHideLoading } = useCommonComponentContext();

    const schemaRequest = object({
        resourceName: multiLangSchema([AvailableLanguage.Vietnamese]).required(),
    });

    const onSubmit = async () => {
        try {
            if (mode === Mode.View) return onChangeMode(Mode.Update);
            onShowLoading();
            
            await schemaRequest.validate(data, { abortEarly: false });
            const request: IRequestUpdateResource = {
                updateTime: data?.updateTime,
                serviceCode: data?.serviceCode,
                resourceType: data?.resourceType,
                adminFeature: data?.adminFeature,
                resourceURI: data?.resourceURI,
                clientId: data?.clientId,
                resourceName: data?.resourceName,
                description: data?.description,
                accessMode: data?.accessMode,
                organizationId: data?.organizationId,
                id: data?.id,
                resourceModuleRequests: (data?.moduleResourceResponses || []).map((el) => ({
                    id: el.id,
                    editMode: el.editMode,
                    resourceCode: el.resourceCode,
                })),
                // extraInformations: data?.extraInformations,
                // resourceDetailRequests: data?.resourceDetailRequests,
            };

            if (Helpers.isNullOrEmpty(request?.id)) {
                delete request.updateTime;
                const result = await ResourceService.create(request);
                onSuccess(Strings.MESSAGE.CREATE_SUCCESS);
            } else {
                const result = await ResourceService.update(request);
                onSuccess(Strings.MESSAGE.UPDATE_SUCCESS);
            }

            onGoBack();
        } catch (error: any) {
            if (error.name === "ValidationError") {
                const newError: IResourceError = Helpers.handleValidationError(error);
                for (const [key, _] of Object.entries(newError)) {
                    if (REQUIRED_MULTI_LANG_KEY.includes(key)) setLanguage(Constants.DefaultLanguage);
                }
                setError(newError);
            } else {
                const e = Helpers.renderExceptionError(error);
                onError(e);
            }
        } finally {
            onHideLoading();
        }
    };

    return {
        onSubmit,
    };
};

export default useSubmit;
